import { Flex } from '@radix-ui/themes'
import {
	Form,
	useLocation,
	useOutletContext,
	useParams,
} from '@remix-run/react'
import * as React from 'react'

import { type ProductNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product'
import { type ProductOptionNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option'
import { type ProductOptionValueNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-option-value'
import { type ProductVariantNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/product-variant'
import { ProductOptionValue } from '~/routes/($lang).products.$slug/product-option-value'
import { PRODUCTS_SLUG_FORM_SELECT_OPTION } from '~/routes/($lang).products.$slug/slug.component'
import { type Context } from '~/routes/_layout/root.component'

/* -------------------------------------------------------------------------------------------------
 * ProductOptions
 * -----------------------------------------------------------------------------------------------*/

type ProductOptionsProps = {
	product: ProductNormalized
	searchParams: URLSearchParams
	selectedVariant: ProductVariantNormalized
}

const ProductOptions: React.FC<ProductOptionsProps> = React.memo(
	({ product, searchParams, selectedVariant }) => {
		const { locale } = useOutletContext<Context>()
		const location = useLocation()
		const params = useParams()

		return (
			<Flex asChild direction="column" justify="center" gapY="3">
				<ul>
					{(product.options as ProductOptionNormalized[])?.map(
						({ code, values, name }) => (
							<li key={code}>
								<Form
									method="get"
									action={`/${locale}${PRODUCTS_SLUG_FORM_SELECT_OPTION.generateAction!(params)}${location.search}`}
									preventScrollReset
								>
									{Array.from(searchParams.entries()).map(([key, value]) =>
										key === code ? null : (
											<input
												key={key + value}
												type="hidden"
												name={key}
												value={value}
											/>
										),
									)}
									<Flex asChild align="center" justify="center" gapX="3">
										<ul>
											{(values as ProductOptionValueNormalized[])
												.sort((a, b) => {
													if (a.value! > b.value!) {
														return -1
													}
													if (a.value! < b.value!) {
														return 1
													}
													return 0
												})
												.map((value) => (
													<ProductOptionValue
														key={value.id}
														searchParams={searchParams}
														code={code!}
														selectedVariant={selectedVariant}
														value={value}
														name={name!}
													/>
												))}
										</ul>
									</Flex>
								</Form>
							</li>
						),
					)}
				</ul>
			</Flex>
		)
	},
)
ProductOptions.displayName = 'ProductOptions'

/* -----------------------------------------------------------------------------------------------*/

export { ProductOptions }
